/**
 * Network adapter — observes GA4 measurement-protocol hits by patching
 * navigator.sendBeacon, window.fetch and XMLHttpRequest.
 *
 * The only surface that sees gtag.js-internal events (page_view,
 * user_engagement) and GA4 Enhanced Measurement. It is also the most brittle:
 * the `en` / `ep.*` encoding is undocumented and can change on Google's side.
 *
 * All three patches are strictly observational — the original call is made with
 * the original arguments and its return value is handed straight back.
 */

import { parseMeasurementProtocol } from '../normalize.js';

/**
 * Default endpoint matcher.
 *
 * Matches on PATH rather than host, because server-side GTM serves the very
 * same payload from a first-party domain (logo.com.tr uses
 * `sgtm.logo.com.tr/g/collect`). Host-only matching on google-analytics.com
 * captures nothing on such sites.
 */
const DEFAULT_COLLECT_PATHS = ['/g/collect', '/mp/collect', '/j/collect'];

/**
 * @param {string} url
 * @param {string[]} extraEndpoints substrings that also identify a GA endpoint
 * @returns {boolean}
 */
export function isCollectUrl(url, extraEndpoints) {
  if (!url) return false;
  const str = String(url);
  for (let i = 0; i < DEFAULT_COLLECT_PATHS.length; i++) {
    if (str.indexOf(DEFAULT_COLLECT_PATHS[i]) !== -1) return true;
  }
  if (extraEndpoints) {
    for (let i = 0; i < extraEndpoints.length; i++) {
      if (extraEndpoints[i] && str.indexOf(extraEndpoints[i]) !== -1) return true;
    }
  }
  return false;
}

/**
 * Split a GA4 hit into one raw event per contained event.
 *
 * GA batches events as newline-separated bodies; the query string carries the
 * parameters shared by every event in the batch.
 *
 * @param {string} url
 * @param {string} [body]
 * @returns {Array<{name: string, params: object, userProps: object}>}
 */
export function parseCollectHit(url, body) {
  const shared = {};
  const qIndex = String(url).indexOf('?');
  if (qIndex !== -1) readPairs(String(url).slice(qIndex + 1), shared);

  const lines = typeof body === 'string' && body.trim()
    ? body.split('\n').filter((l) => l.trim())
    : [];

  const bags = lines.length
    ? lines.map((line) => { const bag = { ...shared }; readPairs(line, bag); return bag; })
    : [shared];

  return bags
    .map((bag) => parseMeasurementProtocol(bag))
    .filter((raw) => raw.name);
}

/**
 * Decode `a=1&b=2` into target. Hand-rolled so the adapter works without
 * URLSearchParams in old embed environments.
 * @param {string} qs
 * @param {object} target
 */
function readPairs(qs, target) {
  qs.split('&').forEach((pair) => {
    if (!pair) return;
    const eq = pair.indexOf('=');
    const rawKey = eq === -1 ? pair : pair.slice(0, eq);
    const rawVal = eq === -1 ? '' : pair.slice(eq + 1);
    try {
      target[decodeURIComponent(rawKey.replace(/\+/g, ' '))] =
        decodeURIComponent(rawVal.replace(/\+/g, ' '));
    } catch (e) {
      target[rawKey] = rawVal;
    }
  });
}

/**
 * @param {object} ctx { emit, log, win, endpoints }
 * @returns {{ name: string, uninstall: Function }|null}
 */
export function installNetworkAdapter(ctx) {
  const win = ctx.win;
  if (!win) return null;

  const endpoints = ctx.endpoints || [];
  const restore = [];

  const observe = (url, body) => {
    try {
      if (!isCollectUrl(url, endpoints)) return;
      parseCollectHit(url, body).forEach((raw) => ctx.emit(raw, 'network'));
    } catch (e) {
      ctx.log('[B2M GA bridge] collect hit skipped', e);
    }
  };

  // ---- navigator.sendBeacon ------------------------------------------------
  const nav = win.navigator;
  if (nav && typeof nav.sendBeacon === 'function' && !nav.sendBeacon.__b2m_ga_bridge) {
    const original = nav.sendBeacon;
    const patched = function (url, data) {
      observe(url, typeof data === 'string' ? data : undefined);
      return original.apply(nav, arguments);
    };
    patched.__b2m_ga_bridge = true;
    patched.__b2m_original = original;
    try {
      nav.sendBeacon = patched;
      restore.push(() => { if (nav.sendBeacon.__b2m_ga_bridge) nav.sendBeacon = original; });
    } catch (e) {
      // Some browsers make sendBeacon read-only — nothing to do but skip it.
      ctx.log('[B2M GA bridge] sendBeacon not patchable', e);
    }
  }

  // ---- fetch ---------------------------------------------------------------
  if (typeof win.fetch === 'function' && !win.fetch.__b2m_ga_bridge) {
    const original = win.fetch;
    const patched = function (input, init) {
      const url = input && typeof input === 'object' && input.url ? input.url : input;
      const body = init && typeof init.body === 'string' ? init.body : undefined;
      observe(url, body);
      return original.apply(this, arguments);
    };
    patched.__b2m_ga_bridge = true;
    patched.__b2m_original = original;
    win.fetch = patched;
    restore.push(() => { if (win.fetch.__b2m_ga_bridge) win.fetch = original; });
  }

  // ---- XMLHttpRequest ------------------------------------------------------
  const XHR = win.XMLHttpRequest;
  if (XHR && XHR.prototype && !XHR.prototype.open.__b2m_ga_bridge) {
    const originalOpen = XHR.prototype.open;
    const originalSend = XHR.prototype.send;

    const patchedOpen = function (method, url) {
      this.__b2m_ga_url = url;
      return originalOpen.apply(this, arguments);
    };
    patchedOpen.__b2m_ga_bridge = true;
    patchedOpen.__b2m_original = originalOpen;

    const patchedSend = function (body) {
      observe(this.__b2m_ga_url, typeof body === 'string' ? body : undefined);
      return originalSend.apply(this, arguments);
    };
    patchedSend.__b2m_ga_bridge = true;
    patchedSend.__b2m_original = originalSend;

    XHR.prototype.open = patchedOpen;
    XHR.prototype.send = patchedSend;
    restore.push(() => {
      if (XHR.prototype.open.__b2m_ga_bridge) XHR.prototype.open = originalOpen;
      if (XHR.prototype.send.__b2m_ga_bridge) XHR.prototype.send = originalSend;
    });
  }

  if (!restore.length) return null;
  ctx.log('[B2M GA bridge] network patches installed');

  return {
    name: 'network',
    uninstall() { restore.forEach((fn) => { try { fn(); } catch (e) { /* ignore */ } }); }
  };
}
